import { User } from "~/common/Store/models/User";
import {
  ExpandedDescriptionTextSectionTypes,
  ProductDetailPageResult,
  SpecificationTable,
  Templates,
  VariationGroup,
} from "../ProductDetailPageResult";
import { getShortenedReviewerName } from "../../common/Reviews/CustomerReviewHelper";
import { CustomerReviews } from "../../common/Reviews/reviewModels";
import { getOfferSchema } from "./OfferSchema";

export interface GroupInStockOptions {
  hasInStock?: boolean;
  hasOutOfStock?: boolean;
  hasExclusive?: boolean;
  hasSpecialOrder?: boolean;
}

export function getSpecification(
  specificationTable: SpecificationTable,
  specificationName: string,
): string {
  if (specificationTable?.specifications) {
    return specificationTable.specifications
      .filter(
        (x: { classification: string }) =>
          x.classification === specificationName,
      )
      ?.map((y) => y.types.map((z) => z.value))
      ?.flat()
      .join(", ");
  }
  return "";
}

export const getAndTrimDescription = (product: ProductDetailPageResult) => {
  const {
    template,
    extendedDescription,
    expandedDescription,
    outletAttributes,
  } = product;
  let description = extendedDescription;

  if (template === Templates.ExpandedDescriptionTemplate) {
    description =
      expandedDescription.sections.find(
        (x) => x.type === ExpandedDescriptionTextSectionTypes.Introduction,
      )?.body || extendedDescription;
  } else if (template === Templates.OutletTemplate) {
    description = outletAttributes?.highlights ?? extendedDescription;
  }
  return description
    .replace(/(<([^>]+)>)/gi, " ")
    .replace(/ +/g, " ")
    .trim();
};

export const getSpecificationProperties = (
  specificationTable: SpecificationTable,
) => {
  return {
    ...(getSpecification(specificationTable, "Color") !== ""
      ? {
          color: getSpecification(specificationTable, "Color"),
        }
      : undefined),
    ...(getSpecification(specificationTable, "Material") !== ""
      ? {
          material: getSpecification(specificationTable, "Material"),
        }
      : undefined),
    ...(getSpecification(specificationTable, "Pattern") !== ""
      ? {
          pattern: getSpecification(specificationTable, "Pattern"),
        }
      : undefined),
  };
};

export const getLargestVariationGroup = (
  variationGroups: VariationGroup[],
): VariationGroup => {
  let highestNumberOfGroupMembers = 0;
  let largestVariationGroup: VariationGroup = {
    displayTypeId: 0,
    isSorted: false,
    hideThumbnails: false,
    optionName: "",
    variationGroupItems: [],
  };

  variationGroups.forEach((group) => {
    if (group.variationGroupItems.length > highestNumberOfGroupMembers) {
      highestNumberOfGroupMembers = group.variationGroupItems.length;
      largestVariationGroup = group;
    }
  });
  return largestVariationGroup;
};

export const getRepeatedProductSchemaProperties = (
  domain: string,
  imageDomain: string,
  product: ProductDetailPageResult,
  user: User,
  isGoogleView: boolean,
  groupInStockOptions?: GroupInStockOptions,
) => {
  const { link, specificationTable } = product;
  const primaryImage =
    product.productImages.find((img) => img.isPrimaryDetailImage) ??
    product.productImages[0];

  return {
    "@type": "Product",
    url: domain + link,
    name: product.header,
    gtin: product.seoUpc,
    image: `${imageDomain}${primaryImage?.large}` || "",
    itemCondition:
      product.template === Templates.OutletTemplate
        ? "https://schema.org/DamagedCondition"
        : "https://schema.org/NewCondition",
    description: getAndTrimDescription(product),
    ...getSpecificationProperties(specificationTable),
    offers: getOfferSchema(
      domain,
      product,
      user,
      isGoogleView,
      groupInStockOptions,
    ),
  };
};

export const updateProductToHaveOnlyLargestVariationGroup = (
  activeProduct: ProductDetailPageResult,
): ProductDetailPageResult => {
  const newActiveProduct = JSON.parse(JSON.stringify(activeProduct));

  if (activeProduct.variationMembership?.variationGroups?.length > 0) {
    const largestVariationGroup = getLargestVariationGroup(
      activeProduct.variationMembership.variationGroups,
    );
    newActiveProduct.variationMembership.variationGroups = [
      largestVariationGroup,
    ];
  }

  return newActiveProduct;
};

export const getProductVariationSchema = (
  activeProduct: ProductDetailPageResult,
  domain: string,
  imageDomain: string,
  user: User,
  isGoogleView: boolean,
) => {
  const variationGroupSchema = [];

  const activeProductSchema = {
    ...getRepeatedProductSchemaProperties(
      domain,
      imageDomain,
      activeProduct,
      user,
      isGoogleView,
    ),
    sku: activeProduct.itemNumber.toLocaleUpperCase(),
  };

  const variationGroupMembersSchema =
    activeProduct.variationMembership?.variationGroups[0]?.variationGroupItems
      .filter((item) => item.itemNumber !== activeProduct.itemNumber)
      .map((item) => {
        return {
          "@type": "Product",
          url: domain + item.seoLink,
        };
      });

  variationGroupSchema.push(activeProductSchema);
  if (variationGroupMembersSchema?.length) {
    variationGroupSchema.push(variationGroupMembersSchema);
  }

  return variationGroupSchema;
};

export function getReviewsForProductSchemas(
  customerReviews: CustomerReviews,
  header: string,
) {
  const reviews = customerReviews.productReviewDetails.filter(
    (review) => review.oneToFiveStarRating > 0,
  );

  return {
    aggregateRating:
      reviews.length > 0
        ? {
            "@type": "AggregateRating",
            itemReviewed: {
              "@type": "Thing",
              name: header,
            },
            reviewCount: reviews.length,
            ratingValue: customerReviews.overallRating.toFixed(1),
          }
        : undefined,
    review:
      reviews.length > 0
        ? reviews.map((review) => ({
            "@type": "Review",
            reviewRating: {
              "@type": "Rating",
              ratingValue: review.oneToFiveStarRating,
            },
            reviewBody: review.textReview.reviewText ?? "",
            author: {
              "@type": "Person",
              name: getShortenedReviewerName(review.fullName),
            },
            datePublished: new Date(
              review.textReview.dateSubmitted,
            ).toLocaleDateString("en-CA"),
          }))
        : undefined,
  };
}
